import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ChevronRight, MessageSquare, RefreshCw } from 'lucide-react';
import type { NextStep } from '../../types/journeys';

interface NextStepsListProps {
  nextSteps: NextStep[];
  onStepClick: (clusterId: number) => void;
  onShowExamples: (fromClusterId: number, toClusterId: number) => void;
  currentClusterId: number;
  loading?: boolean;
}

export default function NextStepsList({
  nextSteps,
  onStepClick,
  onShowExamples,
  currentClusterId,
  loading = false,
}: NextStepsListProps) {
  const formatNumber = (n: number) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1)}M`
      : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}K`
      : n.toString();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (nextSteps.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p className="font-medium">No follow-up topics found</p>
        <p className="text-sm mt-1">This topic doesn't have recorded transitions yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {nextSteps.map((step) => (
        <div
          key={step.cluster.id}
          className="group p-4 rounded-lg border hover:border-blue-300 hover:shadow-sm transition-all bg-white"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h4 className="font-medium text-gray-900 truncate">
                  {step.cluster.label}
                </h4>
                <Badge variant="secondary" className="shrink-0">
                  {step.percentage}%
                </Badge>
              </div>

              <div className="mt-2">
                <Progress value={step.percentage} className="h-2" />
              </div>

              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span>{formatNumber(step.conversation_count)} conversations</span>
              </div>

              {step.example_value && (
                <p className="text-sm text-gray-600 mt-2 line-clamp-2 bg-gray-50 rounded px-2 py-1">
                  "{step.example_value}"
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  onShowExamples(currentClusterId, step.cluster.id);
                }}
              >
                Examples
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => onStepClick(step.cluster.id)}
              >
                Explore
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
